import { IPosition } from '../../types/common.types';
import {
  LocationDefinition,
  PlacedLocation,
  RoomTemplate,
  WallDirection,
} from '../../types/map.types';
import { LOCATION_REGISTRY } from '../../data/locations.config';
import { PlacedRoom } from './placement';

type Grid = number[][];

function inBounds(grid: Grid, x: number, y: number): boolean {
  return y >= 0 && y < grid.length && x >= 0 && x < grid[0].length;
}

function groundCount(grid: Grid): number {
  let n = 0;
  for (let y = 0; y < grid.length; y++) {
    for (let x = 0; x < grid[0].length; x++) {
      if (grid[y][x] === 1) {
        n++;
      }
    }
  }
  return n;
}

/**
 * Reachable ground count from `seed` over 4-connectivity, treating cells in
 * `blocked` (keyed "x,y") as walls. The safety test for a floor location: the
 * floor must stay fully connected once the footprint becomes impassable.
 */
function reachableCount(
  grid: Grid,
  seed: IPosition,
  blocked: Set<string>,
): number {
  const visited = new Set<string>();
  const queue: IPosition[] = [seed];
  visited.add(`${seed.x},${seed.y}`);
  const cardinals = [
    [0, -1],
    [0, 1],
    [-1, 0],
    [1, 0],
  ];
  let count = 0;
  let head = 0;
  while (head < queue.length) {
    const c = queue[head++];
    count++;
    for (const [dx, dy] of cardinals) {
      const nx = c.x + dx;
      const ny = c.y + dy;
      const key = `${nx},${ny}`;
      if (
        !inBounds(grid, nx, ny) ||
        visited.has(key) ||
        blocked.has(key) ||
        grid[ny][nx] === 0
      ) {
        continue;
      }
      visited.add(key);
      queue.push({ x: nx, y: ny });
    }
  }
  return count;
}

function footprintKeys(sx: number, sy: number, w: number, h: number): string[] {
  const keys: string[] = [];
  for (let y = sy; y < sy + h; y++) {
    for (let x = sx; x < sx + w; x++) {
      keys.push(`${x},${y}`);
    }
  }
  return keys;
}

function isInteriorCell(x: number, y: number, grid: Grid): boolean {
  const cardinals = [
    [0, -1],
    [0, 1],
    [-1, 0],
    [1, 0],
  ];
  for (const [dx, dy] of cardinals) {
    if (!inBounds(grid, x + dx, y + dy) || grid[y + dy][x + dx] === 0) {
      return false;
    }
  }
  return true;
}

function firstGroundOutside(
  grid: Grid,
  blocked: Set<string>,
): IPosition | null {
  for (let y = 0; y < grid.length; y++) {
    for (let x = 0; x < grid[0].length; x++) {
      if (grid[y][x] === 1 && !blocked.has(`${x},${y}`)) {
        return { x, y };
      }
    }
  }
  return null;
}

/**
 * Places a floor location inside `room` only where removing its footprint keeps
 * the floor fully connected (flood-fill check) — the fix for the historical
 * "location blocks a corridor / strands a room" bug. Returns null if no safe
 * spot is found.
 */
export function tryPlaceFloorLocationSafe(
  location: LocationDefinition,
  room: PlacedRoom,
  roomIndex: number,
  grid: Grid,
  claimed: Set<string>,
  totalGround: number,
): PlacedLocation | null {
  const candidates = room.cells.filter((c) => isInteriorCell(c.x, c.y, grid));
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }

  for (const c of candidates) {
    const sx = c.x;
    const sy = c.y - location.height + 1;
    const keys = footprintKeys(sx, sy, location.width, location.height);
    const allGroundFree = keys.every((k) => {
      const [kx, ky] = k.split(',').map(Number);
      return inBounds(grid, kx, ky) && grid[ky][kx] === 1 && !claimed.has(k);
    });
    if (!allGroundFree) {
      continue;
    }
    const allInterior = keys.every((k) => {
      const [kx, ky] = k.split(',').map(Number);
      return isInteriorCell(kx, ky, grid);
    });
    if (!allInterior) {
      continue;
    }
    const blocked = new Set(keys);
    const seed = firstGroundOutside(grid, blocked);
    if (!seed) {
      continue;
    }
    const reachable = reachableCount(grid, seed, blocked);
    if (reachable === totalGround - keys.length) {
      for (const k of keys) {
        claimed.add(k);
      }
      return {
        id: location.id,
        x: sx,
        y: sy + location.height - 1,
        width: location.width,
        height: location.height,
        placement: 'floor',
        roomIndex,
        sprite: location.sprite,
        interactionType: location.interactionType,
        spriteAnimationSpeed: location.spriteAnimationSpeed,
      };
    }
  }
  return null;
}

function isAdjacentToGround(
  grid: Grid,
  sx: number,
  sy: number,
  pw: number,
  ph: number,
): boolean {
  const cardinals = [
    [0, -1],
    [0, 1],
    [-1, 0],
    [1, 0],
  ];
  for (let y = sy; y < sy + ph; y++) {
    for (let x = sx; x < sx + pw; x++) {
      for (const [dx, dy] of cardinals) {
        const nx = x + dx;
        const ny = y + dy;
        if (inBounds(grid, nx, ny) && grid[ny][nx] === 1) {
          return true;
        }
      }
    }
  }
  return false;
}

export function tryPlaceWallLocation(
  location: LocationDefinition,
  room: PlacedRoom,
  roomIndex: number,
  grid: Grid,
  claimed: Set<string>,
): PlacedLocation | null {
  const roomCellKeys = new Set(room.cells.map((c) => `${c.x},${c.y}`));
  const cardinals = [
    [0, -1],
    [0, 1],
    [-1, 0],
    [1, 0],
  ];
  const seen = new Set<string>();
  const candidates: { x: number; y: number }[] = [];
  for (const c of room.cells) {
    for (const [dx, dy] of cardinals) {
      const nx = c.x + dx;
      const ny = c.y + dy;
      const key = `${nx},${ny}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      if (
        !inBounds(grid, nx, ny) ||
        grid[ny][nx] !== 0 ||
        roomCellKeys.has(key)
      ) {
        continue;
      }
      candidates.push({ x: nx, y: ny });
    }
  }

  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }

  for (const pos of candidates) {
    const keys = footprintKeys(pos.x, pos.y, location.width, location.height);
    const allWallFree = keys.every((k) => {
      const [kx, ky] = k.split(',').map(Number);
      return inBounds(grid, kx, ky) && grid[ky][kx] === 0 && !claimed.has(k);
    });
    if (
      allWallFree &&
      isAdjacentToGround(grid, pos.x, pos.y, location.width, location.height)
    ) {
      for (const k of keys) {
        claimed.add(k);
      }
      return {
        id: location.id,
        x: pos.x,
        y: pos.y + location.height - 1,
        width: location.width,
        height: location.height,
        placement: 'wall',
        roomIndex,
        sprite: location.sprite,
        interactionType: location.interactionType,
        spriteAnimationSpeed: location.spriteAnimationSpeed,
      };
    }
  }
  return null;
}

export function tryPlaceDirectionalWallLocation(
  location: LocationDefinition,
  room: PlacedRoom,
  roomIndex: number,
  grid: Grid,
  claimed: Set<string>,
): PlacedLocation | null {
  const span = location.wallSpan ?? 1;

  const directionGeom: Record<
    WallDirection,
    { groundDx: number; groundDy: number; spanDx: number; spanDy: number }
  > = {
    top: { groundDx: 0, groundDy: 1, spanDx: 1, spanDy: 0 },
    bottom: { groundDx: 0, groundDy: -1, spanDx: 1, spanDy: 0 },
    left: { groundDx: 1, groundDy: 0, spanDx: 0, spanDy: 1 },
    right: { groundDx: -1, groundDy: 0, spanDx: 0, spanDy: 1 },
  };
  const directionList: WallDirection[] = ['top', 'bottom', 'left', 'right'];

  const roomCellKeys = new Set(room.cells.map((c) => `${c.x},${c.y}`));
  const cardinals = [
    [0, -1],
    [0, 1],
    [-1, 0],
    [1, 0],
  ];
  const seen = new Set<string>();
  const candidates: { x: number; y: number }[] = [];
  for (const c of room.cells) {
    for (const [dx, dy] of cardinals) {
      const nx = c.x + dx;
      const ny = c.y + dy;
      const key = `${nx},${ny}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      if (
        !inBounds(grid, nx, ny) ||
        grid[ny][nx] !== 0 ||
        roomCellKeys.has(key) ||
        claimed.has(key)
      ) {
        continue;
      }
      candidates.push({ x: nx, y: ny });
    }
  }

  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }

  for (const start of candidates) {
    const dirsShuffled = [...directionList];
    for (let i = dirsShuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [dirsShuffled[i], dirsShuffled[j]] = [dirsShuffled[j], dirsShuffled[i]];
    }

    for (const direction of dirsShuffled) {
      const geom = directionGeom[direction];
      let valid = true;
      for (let i = 0; i < span; i++) {
        const cx = start.x + i * geom.spanDx;
        const cy = start.y + i * geom.spanDy;
        if (
          !inBounds(grid, cx, cy) ||
          grid[cy][cx] !== 0 ||
          claimed.has(`${cx},${cy}`)
        ) {
          valid = false;
          break;
        }
        const gx = cx + geom.groundDx;
        const gy = cy + geom.groundDy;
        if (!inBounds(grid, gx, gy) || grid[gy][gx] !== 1) {
          valid = false;
          break;
        }
      }
      if (!valid) {
        continue;
      }

      const isHorizontal = direction === 'top' || direction === 'bottom';
      const width = isHorizontal ? span : 1;
      const height = isHorizontal ? 1 : span;

      const keys = footprintKeys(start.x, start.y, width, height);
      for (const k of keys) {
        claimed.add(k);
      }
      return {
        id: location.id,
        x: start.x,
        y: start.y + height - 1,
        width,
        height,
        placement: 'wall',
        roomIndex,
        sprite: location.sprite,
        interactionType: location.interactionType,
        direction,
        spriteAnimationSpeed: location.spriteAnimationSpeed,
      };
    }
  }
  return null;
}

export function placeTemplateLocations(
  placed: PlacedRoom[],
  roomTemplates: RoomTemplate[],
  claimed: Set<string>,
): PlacedLocation[] {
  const out: PlacedLocation[] = [];
  for (const room of placed) {
    if (!room.templateId) {
      continue;
    }
    const template = roomTemplates.find((t) => t.id === room.templateId);
    if (!template) {
      continue;
    }
    const roomIndex = placed.indexOf(room);
    for (const slot of template.locations) {
      const def = LOCATION_REGISTRY[slot.locationId];
      if (!def) {
        continue;
      }
      const blX = room.x + slot.x;
      const blY = room.y + slot.y;
      const tlY = blY - def.height + 1;
      const keys = footprintKeys(blX, tlY, def.width, def.height);
      for (const k of keys) {
        claimed.add(k);
      }
      out.push({
        id: def.id,
        x: blX,
        y: blY,
        width: def.width,
        height: def.height,
        placement: def.placement,
        roomIndex,
        sprite: def.sprite,
        interactionType: def.interactionType,
        spriteAnimationSpeed: def.spriteAnimationSpeed,
      });
    }
  }
  return out;
}

function tryPlaceLocationSafe(
  location: LocationDefinition,
  room: PlacedRoom,
  roomIndex: number,
  grid: Grid,
  claimed: Set<string>,
  totalGround: number,
): PlacedLocation | null {
  if (location.placement === 'floor') {
    return tryPlaceFloorLocationSafe(
      location,
      room,
      roomIndex,
      grid,
      claimed,
      totalGround,
    );
  }
  if (location.wallDirectional) {
    return tryPlaceDirectionalWallLocation(
      location,
      room,
      roomIndex,
      grid,
      claimed,
    );
  }
  return tryPlaceWallLocation(location, room, roomIndex, grid, claimed);
}

// Mirrors tryPlaceFloorLocationSafe but skips the connectivity flood-fill.
// Last resort for mandatory locations (e.g. stairs-down): a missing mandatory
// location makes the floor unwinnable, so we drop the connectivity guarantee.
function tryPlaceFloorLocationRelaxed(
  location: LocationDefinition,
  room: PlacedRoom,
  roomIndex: number,
  grid: Grid,
  claimed: Set<string>,
): PlacedLocation | null {
  const candidates = room.cells.filter((c) => isInteriorCell(c.x, c.y, grid));
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }

  for (const c of candidates) {
    const sx = c.x;
    const sy = c.y - location.height + 1;
    const keys = footprintKeys(sx, sy, location.width, location.height);
    const allGroundFree = keys.every((k) => {
      const [kx, ky] = k.split(',').map(Number);
      return inBounds(grid, kx, ky) && grid[ky][kx] === 1 && !claimed.has(k);
    });
    if (!allGroundFree) {
      continue;
    }
    const allInterior = keys.every((k) => {
      const [kx, ky] = k.split(',').map(Number);
      return isInteriorCell(kx, ky, grid);
    });
    if (!allInterior) {
      continue;
    }
    for (const k of keys) {
      claimed.add(k);
    }
    return {
      id: location.id,
      x: sx,
      y: sy + location.height - 1,
      width: location.width,
      height: location.height,
      placement: 'floor',
      roomIndex,
      sprite: location.sprite,
      interactionType: location.interactionType,
      spriteAnimationSpeed: location.spriteAnimationSpeed,
    };
  }
  return null;
}

export function placeLocations(
  grid: Grid,
  placed: PlacedRoom[],
  templatedRoomIndices: Set<number>,
  locations: LocationDefinition[],
  claimed: Set<string>,
): PlacedLocation[] {
  const candidates = locations.filter((l) => !l.templateOnly);
  const totalGround = groundCount(grid);

  const result: PlacedLocation[] = [];
  const placedCounts = new Map<string, number>();

  const shuffledRoomIndices = placed
    .map((_, i) => i)
    .filter((i) => !templatedRoomIndices.has(i));
  for (let i = shuffledRoomIndices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledRoomIndices[i], shuffledRoomIndices[j]] = [
      shuffledRoomIndices[j],
      shuffledRoomIndices[i],
    ];
  }

  // --- Pass 1: mandatory locations (exactly one each) ---
  // Each mandatory draws its own permutation. Without it, all mandatories
  // iterate the same list from index 0 and systematically land in the first
  // room (which almost always has enough space to host them all), which used
  // to make the stairs always appear in the spawn room. With a per-location
  // permutation, a same-room collision becomes a random event again (~1/N).
  const mandatory = candidates.filter((l) => l.mandatory);
  for (const loc of mandatory) {
    const order = [...shuffledRoomIndices];
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    let placedResult: PlacedLocation | null = null;
    for (const ri of order) {
      const room = placed[ri];
      const res = tryPlaceLocationSafe(
        loc,
        room,
        ri,
        grid,
        claimed,
        totalGround,
      );
      if (res) {
        placedResult = res;
        break;
      }
    }
    if (!placedResult && loc.placement === 'floor') {
      // Safe pass failed every room: relax the connectivity gate so a mandatory
      // floor location (e.g. stairs-down) is never silently dropped.
      for (const ri of order) {
        const room = placed[ri];
        const res = tryPlaceFloorLocationRelaxed(loc, room, ri, grid, claimed);
        if (res) {
          placedResult = res;
          break;
        }
      }
    } else if (!placedResult && loc.placement !== 'floor') {
      // Wall-type mandatory: wall placers have no connectivity gate, so a second
      // pass with the same placer is the correct relaxed fallback.
      for (const ri of order) {
        const room = placed[ri];
        const res = tryPlaceLocationSafe(
          loc,
          room,
          ri,
          grid,
          claimed,
          totalGround,
        );
        if (res) {
          placedResult = res;
          break;
        }
      }
    }
    if (placedResult) {
      result.push(placedResult);
      placedCounts.set(loc.id, 1);
    }
  }

  // --- Pass 2: optional locations by weighted random ---
  const optional = candidates.filter((l) => !l.mandatory);
  if (optional.length === 0) {
    return result;
  }

  const totalWeight = optional.reduce((sum, l) => sum + l.weight, 0);
  if (totalWeight <= 0) {
    return result;
  }

  for (const ri of shuffledRoomIndices) {
    const room = placed[ri];

    const roll = Math.random() * totalWeight;
    let acc = 0;
    for (const loc of optional) {
      acc += loc.weight;
      if (roll > acc) {
        continue;
      }

      const count = placedCounts.get(loc.id) ?? 0;
      if (loc.unique && count >= 1) {
        break;
      }
      if (loc.maxCount !== undefined && count >= loc.maxCount) {
        break;
      }

      const res = tryPlaceLocationSafe(
        loc,
        room,
        ri,
        grid,
        claimed,
        totalGround,
      );
      if (res) {
        result.push(res);
        placedCounts.set(loc.id, count + 1);
      }
      break;
    }
  }

  return result;
}
